import { useEffect, useMemo } from 'react'
import { useStore } from '@/lib/store'
import { useLang } from '@/lib/i18n'
import { autoFeature, featureCacheKey, featureLive, featureNeedMet, featureOn, FEATURES, type FeatureKey, type FeatureMeta } from '@/lib/features'

/**
 * „მარტივი რეჟიმი": is this feature on screen for this shop — the owner's
 * choice when he made one, else the shop's own behaviour (lib/features.ts).
 * Returns a boolean, so a component re-renders only when the answer flips.
 *
 * The behaviour is read from customers and sales, which are not kept on the
 * device between starts. Until they arrive (and while offline) the last
 * answer this device saw stands in — otherwise ნისია would vanish and
 * reappear on every start of a shop that uses it. 153: kept per user and
 * shop (featureCacheKey) — a shared device never carries one shop's answer
 * into another's till.
 */
function readCache(key: string | null): Partial<Record<FeatureKey, boolean>> {
  if (!key) return {}
  try {
    const v = JSON.parse(localStorage.getItem(key) ?? '{}')
    return v && typeof v === 'object' ? v : {}
  } catch {
    return {}
  }
}

export function useFeature(key: FeatureKey): boolean {
  const live = useStore((s) => featureLive(s))
  // 153 (Codex): the website and its orders are the owner's — never a support session's
  const inSupport = useStore((s) => s.supportSession != null)
  const pref = useStore((s) => (key === 'discount' ? s.settings.posDiscount : s.settings.features?.[key]))
  const auto = useStore((s) => autoFeature(key, {
    customers: s.customers,
    sales: s.sales,
    cashiers: s.cashiers,
    products: s.products,
    productConversions: s.productConversions,
    productBreakdowns: s.productBreakdowns,
    promotions: s.promotions,
    shopType: s.settings.shopType,
    hasStore: s.storeSite != null && !s.supportSession,
  }).on)
  const cacheKey = useStore((s) => featureCacheKey(s.cachedUserId, s.currentOrg?.id))

  useEffect(() => {
    if (!live || !cacheKey) return
    const cache = readCache(cacheKey)
    if (cache[key] === auto) return
    try { localStorage.setItem(cacheKey, JSON.stringify({ ...cache, [key]: auto })) } catch { /* private window */ }
  }, [live, auto, key, cacheKey])

  // 1.40 (Codex) — askQty reads only the shop's type, which the first boot
  // phase already has: no device copy can stand in for it, or the till would
  // ask with an old answer while Settings shows the new one.
  const cached = live || key === 'askQty' ? undefined : readCache(cacheKey)[key]
  // 1.69 — a switch that needs another is off while that one is off (the table QR without the tables)
  const needMet = useStore((s) => featureNeedMet(key, s.settings.features))
  if (key === 'store' && inSupport) return false
  if (!needMet) return false
  return featureOn(pref, { on: cached ?? auto, why: '' })
}

export type FeatureState = FeatureMeta & { on: boolean; chosen: boolean; auto: { on: boolean; why: string }; needMet: boolean }

/** Every feature with its state and the reason — the Settings card. */
export function useFeatureStates(): FeatureState[] {
  const customers = useStore((s) => s.customers)
  const sales = useStore((s) => s.sales)
  const cashiers = useStore((s) => s.cashiers)
  const products = useStore((s) => s.products)
  const productConversions = useStore((s) => s.productConversions)
  const productBreakdowns = useStore((s) => s.productBreakdowns)
  const promotions = useStore((s) => s.promotions)
  const settings = useStore((s) => s.settings)
  const hasStore = useStore((s) => s.storeSite != null && !s.supportSession)
  const lang = useLang()   // the reasons are words in the interface language
  return useMemo(() => {
    const usage = { customers, sales, cashiers, products, productConversions, productBreakdowns, promotions, shopType: settings.shopType, hasStore }
    return FEATURES.map((f) => {
      const auto = autoFeature(f.key, usage)
      const pref = f.key === 'discount' ? settings.posDiscount : settings.features?.[f.key]
      const needMet = featureNeedMet(f.key, settings.features)
      return { ...f, auto, chosen: pref === true || pref === false, on: needMet && featureOn(pref, auto), needMet }
    })
  }, [customers, sales, cashiers, products, productConversions, productBreakdowns, promotions, settings, hasStore, lang])
}
